import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getSafeToken } from '../../utils/tokenHelper';
import { useAuth } from '../../context/AuthContext';
import { VERTEX_API_BASE } from '../../utils/vertexApiHelper';
import {
  ArrowLeft,
  Copy,
  Loader2,
  AlertCircle,
  CheckCircle,
  PenTool,
  Minus,
  Plus
} from 'lucide-react';

const SIMULATION_TYPES = [
  { value: 'negotiation', label: 'negotiation' },
  { value: 'leadership', label: 'leadership' },
  { value: 'conflict', label: 'conflict' },
  { value: 'evaluation', label: 'evaluation' },
  { value: 'other', label: 'other' }
];

const PRESSURE_LEVELS = [
  { value: 'low', label: 'low' },
  { value: 'medium', label: 'medium' },
  { value: 'high', label: 'high' }
];

const isBlank = (v) => typeof v !== 'string' || v.trim().length === 0;

function safeJson(text) {
  return JSON.stringify(text, null, 2);
}

function buildBlocksFromComposerOutput(composerOutput) {
  if (!composerOutput) return [];

  const blocks = [];

  blocks.push({
    id: 'roleDefinition_base',
    title: 'roleDefinition_base',
    text: String(composerOutput.roleDefinition_base || '')
  });

  blocks.push({
    id: 'brief_schema',
    title: 'brief_schema',
    text: safeJson(composerOutput.brief_schema || {})
  });

  if (composerOutput.scenarioPromptPack) {
    blocks.push({
      id: 'scenarioPromptPack',
      title: 'scenarioPromptPack',
      text: safeJson(composerOutput.scenarioPromptPack || {})
    });
  }

  const phases = Array.isArray(composerOutput.phasePrompts) ? composerOutput.phasePrompts : [];
  for (let i = 0; i < phases.length; i += 1) {
    const p = phases[i] || {};
    blocks.push({
      id: `phasePrompts.${p.phaseId || i}`,
      title: `phasePrompts[${p.phaseId || `PHASE_${i + 1}`}]`,
      text: safeJson({
        phaseId: p.phaseId,
        onEnterPrompt: p.onEnterPrompt,
        phasePrompt: p.phasePrompt,
        onExitPrompt: p.onExitPrompt
      })
    });
  }

  if (composerOutput.coach_prompt_base) {
    blocks.push({
      id: 'coach_prompt_base',
      title: 'coach_prompt_base',
      text: String(composerOutput.coach_prompt_base || '')
    });
  }

  if (composerOutput.moderator_prompt_base) {
    blocks.push({
      id: 'moderator_prompt_base',
      title: 'moderator_prompt_base',
      text: String(composerOutput.moderator_prompt_base || '')
    });
  }

  return blocks;
}

export default function ComposerView() {
  const navigate = useNavigate();
  const { agentId } = useParams();
  const { user, token } = useAuth();

  const [loadingAgent, setLoadingAgent] = useState(false);
  const [agentError, setAgentError] = useState(null);

  const [inputs, setInputs] = useState({
    simulationType: 'negotiation',
    userRole: '', // UI: participantRole
    clientRole: '', // UI: counterpartRole
    pedagogicalObjective: '',
    phaseCount: 2,
    pressureLevel: 'medium'
  });

  const [generatedOutput, setGeneratedOutput] = useState(null);
  const [qa, setQa] = useState({ status: 'IDLE', errors: [], warnings: [], lastError: null });
  const [qaLoading, setQaLoading] = useState(false);

  const [copiedId, setCopiedId] = useState(null);
  const [copyError, setCopyError] = useState(null);

  const isValid = useMemo(() => {
    return (
      !isBlank(inputs.simulationType) &&
      !isBlank(inputs.userRole) &&
      !isBlank(inputs.clientRole) &&
      !isBlank(inputs.pedagogicalObjective) &&
      Number(inputs.phaseCount) >= 1 &&
      Number(inputs.phaseCount) <= 6 &&
      !isBlank(inputs.pressureLevel)
    );
  }, [inputs]);

  useEffect(() => {
    if (!agentId) return;

    const run = async () => {
      try {
        setLoadingAgent(true);
        setAgentError(null);

        const authToken = token || getSafeToken();
        const headers = { 'Content-Type': 'application/json' };
        if (authToken) headers.Authorization = `Bearer ${authToken}`;

        const resp = await fetch(`${VERTEX_API_BASE}/v4/agents/${agentId}`, { headers });
        const data = await resp.json().catch(() => ({}));

        if (!resp.ok || !data?.success || !data?.agent) {
          setAgentError(data?.message || 'No se pudo cargar el agente.');
          return;
        }

        const agent = data.agent;
        setInputs((prev) => ({
          ...prev,
          // Mantener keys existentes: userRole/clientRole/pedagogicalObjective/phaseCount
          userRole: agent?.wizardMeta?.lastApply?.definitionInputs?.participantRole || prev.userRole,
          clientRole: agent?.wizardMeta?.lastApply?.definitionInputs?.counterpartRole || prev.clientRole,
          pedagogicalObjective: agent?.wizardMeta?.lastApply?.definitionInputs?.pedagogicalObjective || prev.pedagogicalObjective,
          phaseCount: Number(agent?.wizardMeta?.lastApply?.definitionInputs?.phaseCount || prev.phaseCount || 2),
          simulationType: agent?.wizardMeta?.lastApply?.definitionInputs?.simulationType || prev.simulationType,
          pressureLevel: agent?.wizardMeta?.lastApply?.definitionInputs?.pressureLevel || prev.pressureLevel
        }));
      } catch (e) {
        setAgentError('Error de conexión al cargar el agente.');
      } finally {
        setLoadingAgent(false);
      }
    };

    run();
  }, [agentId, token]);

  const qaStyles = {
    OK: 'bg-green-100 text-green-800 border-green-200',
    WARN: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    FAIL: 'bg-red-100 text-red-800 border-red-200',
    ERROR: 'bg-red-100 text-red-800 border-red-200',
    IDLE: 'bg-slate-100 text-slate-700 border-slate-200'
  };

  const onChange = (key) => (e) => {
    const nextValue = e.target.value;
    setInputs((prev) => ({
      ...prev,
      [key]: key === 'phaseCount' ? Number(nextValue) : nextValue
    }));
  };

  const adjustPhase = (delta) => {
    setInputs((prev) => {
      const next = Math.max(1, Math.min(6, (Number(prev.phaseCount) || 1) + delta));
      return { ...prev, phaseCount: next };
    });
  };

  const runGenerate = async () => {
    try {
      setQaLoading(true);
      setQa({ status: 'IDLE', errors: [], warnings: [], lastError: null });

      const authToken = token || getSafeToken();
      const headers = {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
      };

      const resp = await fetch(`${VERTEX_API_BASE}/v4/composer/generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          inputs: {
            simulationType: inputs.simulationType,
            participantRole: inputs.userRole,
            counterpartRole: inputs.clientRole,
            pedagogicalObjective: inputs.pedagogicalObjective,
            pressureLevel: inputs.pressureLevel,
            phaseCount: inputs.phaseCount
          }
        })
      });

      const data = await resp.json().catch(() => ({}));
      const qaFromServer = data?.qa || null;

      if (!qaFromServer) {
        setQa({ status: 'ERROR', errors: [], warnings: [], lastError: 'Respuesta QA inválida.' });
        return { ok: false, output: null };
      }

      const serverStatus = String(qaFromServer.status || '').toLowerCase();
      const errors = Array.isArray(qaFromServer.errors) ? qaFromServer.errors : [];
      const warnings = Array.isArray(qaFromServer.warnings) ? qaFromServer.warnings : [];

      const outputFromServer = data?.composerOutput || data?.rawComposerOutput || null;

      if (!resp.ok || serverStatus === 'fail') {
        setQa({ status: 'FAIL', errors, warnings, lastError: null });
        // Ticket (P0): no bloquear copiar. Si el server devuelve output, mostrarlo.
        return { ok: false, output: outputFromServer };
      }

      setQa({ status: warnings.length > 0 ? 'WARN' : 'OK', errors: [], warnings, lastError: null });
      return { ok: true, output: outputFromServer };
    } catch (e) {
      setQa({ status: 'ERROR', errors: [], warnings: [], lastError: 'No se pudo ejecutar Build QA.' });
      return { ok: false, output: null };
    } finally {
      setQaLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!isValid) {
      setQa({ status: 'ERROR', errors: [], warnings: [], lastError: 'Completa los inputs obligatorios.' });
      return;
    }

    setGeneratedOutput(null);
    const result = await runGenerate();
    setGeneratedOutput(result.output);
  };

  const blocks = useMemo(() => buildBlocksFromComposerOutput(generatedOutput), [generatedOutput]);

  const copyBlock = async (block) => {
    try {
      setCopyError(null);
      await navigator.clipboard.writeText(block.text);
      setCopiedId(block.id);
      setTimeout(() => setCopiedId(null), 1200);
    } catch (e) {
      setCopyError('No se pudo copiar al portapapeles.');
    }
  };

  return (
    <div className="h-full bg-gray-50 overflow-y-auto">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/vertex/builder')}
                className="p-2 hover:bg-gray-100 rounded-lg"
                title="Volver"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <PenTool className="w-5 h-5 text-indigo-600" />
                  Prompt Pack (Composer)
                </h1>
                <p className="text-sm text-gray-600">Vista única para generar y copiar prompts. No guarda ni aplica cambios.</p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleGenerate}
              disabled={!isValid || qaLoading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {qaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenTool className="w-4 h-4" />}
              Generar Prompt Pack
            </button>
          </div>

          {agentId && (
            <div className="mt-3 text-xs text-gray-500">
              {loadingAgent ? 'Cargando agente…' : agentError ? agentError : `Modo: Composer sobre agente existente (${agentId}).`}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Sección 1: Inputs mínimos */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <h2 className="text-sm font-semibold text-gray-900">1) Inputs mínimos</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">simulationType</label>
              <select
                value={inputs.simulationType}
                onChange={onChange('simulationType')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                {SIMULATION_TYPES.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-600 mt-1">Define el tipo de interacción (no la habilidad).</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">pressureLevel</label>
              <select
                value={inputs.pressureLevel}
                onChange={onChange('pressureLevel')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                {PRESSURE_LEVELS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-600 mt-1">Nivel de presión conversacional esperado.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">participantRole</label>
              <input
                type="text"
                value={inputs.userRole}
                onChange={onChange('userRole')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Ej: Account Executive de un proveedor SaaS"
              />
              <p className="text-xs text-gray-600 mt-1">Rol del participante humano.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">counterpartRole</label>
              <input
                type="text"
                value={inputs.clientRole}
                onChange={onChange('clientRole')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Ej: Director(a) de Compras de una empresa retail"
              />
              <p className="text-xs text-gray-600 mt-1">Rol de la contraparte (cliente IA).</p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">pedagogicalObjective</label>
              <input
                type="text"
                value={inputs.pedagogicalObjective}
                onChange={onChange('pedagogicalObjective')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Ej: Practicar diagnóstico, preguntas y propuesta con trade-offs"
              />
              <p className="text-xs text-gray-600 mt-1">Qué habilidad o resultado se entrena.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">phaseCount (1–6)</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => adjustPhase(-1)}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  title="-"
                >
                  <Minus className="w-4 h-4 text-gray-700" />
                </button>
                <input
                  type="number"
                  min={1}
                  max={6}
                  value={inputs.phaseCount}
                  onChange={onChange('phaseCount')}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <button
                  type="button"
                  onClick={() => adjustPhase(1)}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  title="+"
                >
                  <Plus className="w-4 h-4 text-gray-700" />
                </button>
              </div>
              <p className="text-xs text-gray-600 mt-1">Número de fases para progresión (1–6).</p>
            </div>
          </div>

          {!isValid && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700">
              Completa los campos obligatorios para generar el Prompt Pack.
            </div>
          )}
        </div>

        {/* Sección 2: QA */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-sm font-semibold text-gray-900">2) QA (siempre visible)</h2>
            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${qaStyles[qa.status] || qaStyles.IDLE}`}> 
              {qa.status}
            </span>
          </div>

          {qa.status === 'FAIL' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm text-red-800 font-medium">FAIL (P0): no usable para exportar/usar.</p>
                <p className="text-sm text-red-700">Puedes copiar manualmente, pero corrige estos checks antes de reutilizar.</p>
              </div>
            </div>
          )}

          {qa.status === 'OK' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-700" />
              <p className="text-sm text-green-800">OK: sin bloqueos.</p>
            </div>
          )}

          {qa.lastError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <p className="text-sm text-red-700">{qa.lastError}</p>
            </div>
          )}

          {qa.errors.length > 0 && (
            <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
              {qa.errors.map((e, idx) => (
                <li key={idx}>
                  <span className="text-xs font-semibold text-red-700 mr-2">P0</span>
                  <span className="font-mono text-xs">{e.checkId}</span>: {e.message}
                </li>
              ))}
            </ul>
          )}

          {qa.warnings.length > 0 && (
            <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
              {qa.warnings.map((w, idx) => (
                <li key={idx}>
                  <span className="text-xs font-semibold text-yellow-700 mr-2">P1</span>
                  <span className="font-mono text-xs">{w.checkId}</span>: {w.message}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Sección 3: Output */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="border-b border-gray-200 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-900">3) Output: Prompt Pack</h2>
            <p className="text-xs text-gray-600 mt-1">Bloques 1:1 copiabes. No se aplican cambios automáticamente.</p>
          </div>

          <div className="p-4 space-y-4">
            {copyError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-700">{copyError}</p>
              </div>
            )}

            {blocks.length === 0 ? (
              <div className="text-sm text-gray-600">Genera un Prompt Pack para ver los bloques aquí.</div>
            ) : (
              <div className="space-y-4">
                {blocks.map((b) => (
                  <div key={b.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 border-b border-gray-200">
                      <div className="text-sm font-semibold text-gray-900">{b.title}</div>
                      <button
                        type="button"
                        onClick={() => copyBlock(b)}
                        className="inline-flex items-center gap-2 px-3 py-2 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                      >
                        <Copy className="w-4 h-4" />
                        {copiedId === b.id ? 'Copiado' : 'Copiar'}
                      </button>
                    </div>
                    <textarea
                      readOnly
                      value={b.text}
                      className="w-full p-3 text-xs font-mono text-gray-800 bg-white resize-y min-h-[120px]"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sección 4: Cómo usar */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-2">4) Cómo usar</h2>
          <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
            <li>Copia <span className="font-mono text-xs">roleDefinition_base</span> y pégalo en la pestaña correspondiente del Builder.</li>
            <li>Copia <span className="font-mono text-xs">phasePrompts</span> por fase y pégalo en la State Machine.</li>
            <li>Copia <span className="font-mono text-xs">brief_schema</span> / coach / moderator donde corresponda.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
